/**
 * ITEM CHIP COMPONENT
 *
 * Small clickable card showing an item attached to a message or pinned to conversation
 */

import React from 'react';
import type { AttachedItem } from '../../messaging/types';
import { AppIcons } from '../../constants/icons';

interface ItemChipProps {
  item: AttachedItem;
  size?: 'small' | 'medium';
  showStatus?: boolean;
  onClick?: () => void;
}

const ItemChip: React.FC<ItemChipProps> = ({ 
  item, 
  size = 'small',
  showStatus = false,
  onClick 
}) => {
  const getStatusColor = () => {
    switch (item.status) {
      case 'available': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'sold': return 'bg-red-500';
      default: return 'bg-green-500';
    }
  };

  const getStatusLabel = () => {
    switch (item.status) {
      case 'available': return 'Available';
      case 'pending': return 'Pending';
      case 'sold': return 'Sold';
      default: return 'Available';
    }
  };

  const getStatusIcon = () => {
    return <AppIcons.Available className="w-3 h-3 text-current inline-block mr-1" aria-label={item.status || 'available'} />;
  };

  if (size === 'medium') {
    return (
      <div
        onClick={onClick}
        className={`glass-card-sm overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
        style={{ width: '120px' }}
      >
        {/* Image */}
        {item.photoUrl ? (
          <img 
            src={item.photoUrl} 
            alt={item.name}
            className="w-full h-20 object-cover"
          />
        ) : (
          <div className="w-full h-20 rainbow-gradient flex items-center justify-center">
            <AppIcons.Polish className="w-8 h-8 text-current" aria-label="Nail Polish" />
          </div>
        )}
        
        {/* Info */}
        <div className="p-2">
          <p className="text-xs text-glass font-bold truncate font-heading">
            {item.brand}
          </p>
          <p className="text-sm font-bold text-glass truncate font-body">
            {item.name}
          </p>


          {showStatus && (
            <p className="text-xs mt-1 font-body flex items-center">
              {getStatusIcon()}
              {getStatusLabel()}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Small size (default)
  return (
    <div
      onClick={onClick}
      className={`inline-flex items-center gap-2 bg-slate-800/40 rounded-lg px-2 py-1 ${onClick ? 'cursor-pointer hover:bg-slate-700/60 transition-default' : ''}`}
    >
      {/* Tiny thumbnail */}
      {item.photoUrl ? (
        <img 
          src={item.photoUrl} 
          alt={item.name}
          className="w-8 h-8 object-cover rounded"
        />
      ) : (
        <div className="w-8 h-8 rainbow-gradient rounded flex items-center justify-center">
          <AppIcons.Polish className="w-4 h-4 text-current" aria-label="Nail Polish" />
        </div>
      )}
      
      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-glass truncate font-body">
          {item.name}
        </p>
        <p className="text-xs text-glass-muted truncate font-body">
          {item.brand}
        </p>
      </div>
      
      {/* Status dot */}
      {showStatus && (
        <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} title={item.status} />
      )}
    </div>
  );
};

export default ItemChip;
